import { useEffect, useState } from 'react';
import { Alert, Button, LinearProgress } from '@mui/material';
import { Stack, Typography } from './mui-system';
import { api } from '../api/client';
import type { AiSelectionPlan, AiSelectionScope } from '../../../api/src/fakes/serp-ai.types';
import { fakeError } from './fakes-presentation';
export function SerpAiBatchLaunch({
  workspaceId,
  scope,
  busy,
  launchable,
  revision,
  remainingUsd,
  act,
  onStarted,
  refreshKey,
}: {
  workspaceId: string;
  scope: AiSelectionScope;
  busy: boolean;
  launchable: boolean;
  revision: number;
  remainingUsd: number;
  act: (work: () => Promise<void>) => Promise<void>;
  onStarted: (id: string) => void;
  refreshKey: number;
}) {
  const [plan, setPlan] = useState<AiSelectionPlan | null>(null);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  const { groupId, runId, top, changes } = scope;
  useEffect(() => {
    let live = true;
    setPlan(null);
    setError('');
    void api.serpAiPlan(workspaceId, { groupId, runId, top, changes }).then(
      (value) => {
        if (live) setPlan(value);
      },
      (e) => {
        if (live) setError(fakeError(e));
      },
    );
    return () => {
      live = false;
    };
  }, [workspaceId, groupId, runId, top, changes, version, refreshKey]);
  return (
    <Stack gap={1}>
      <Typography fontWeight={600}>Вся выбранная выдача</Typography>
      {!plan && !error && <LinearProgress aria-label="Расчёт всей выдачи" />}
      {error && <Alert severity="error">{error}</Alert>}
      {plan && (
        <>
          <Typography>
            {plan.label} · запросов: {plan.queryCount} · неразобранных URL без повторов:{' '}
            {plan.total}.
          </Typography>
          <Typography fontSize={12}>
            Все страницы таблицы. Разбор продолжится после закрытия окна. Уже сохранённые анализы
            используются повторно.
          </Typography>
          <Typography fontSize={12}>
            Верхняя оценка для всех URL: ${plan.maxReserveUsd.toFixed(4)}. Расход ограничен остатком
            общего бюджета ${remainingUsd.toFixed(2)}; при его исчерпании оставшаяся работа
            остановится. Текст доступных страниц будет передан в OpenAI.
          </Typography>
        </>
      )}
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button
          variant="contained"
          disabled={busy || !launchable || !plan?.total}
          onClick={() =>
            void act(async () => {
              if (!plan) return;
              try {
                const batch = await api.startSerpAiBatch(workspaceId, {
                  groupId,
                  runId,
                  top,
                  changes,
                  fingerprint: plan.fingerprint,
                  profileRevision: revision,
                });
                onStarted(batch.id);
              } finally {
                setVersion((v) => v + 1);
              }
            })
          }
        >
          Разобрать всю выдачу{plan ? ` (${plan.total})` : ''}
        </Button>
        <Button disabled={busy} onClick={() => setVersion((v) => v + 1)}>
          Обновить расчёт
        </Button>
      </Stack>
    </Stack>
  );
}
